/**
 * Waits for an iframe's JavaScript to be ready and calls the initialize function
 * @param iframeRef - Reference to the iframe element
 * @param initData - Data to pass to the initialize function
 * @param callback - Callback function to handle the response
 * @param options - Additional options for initialization
 */
export function waitForIframeInitialize<InitT = any, CbT = any>(
  iframeRef: HTMLIFrameElement | null,
  initData: InitT,
  embedLocation: string,
  callback?: (data?: CbT) => void,
  options: {
    maxAttempts?: number
    retryInterval?: number
    componentName?: string
  } = {},
) {
  if (!iframeRef) return

  const { maxAttempts = 50, retryInterval = 100, componentName = 'Component' } = options

  const tryInitialize = (attempts = 0) => {
    if (!iframeRef.contentWindow) return

    // Check if the initialize function exists in the iframe
    if ((iframeRef.contentWindow as any)?.initialize) {
      // Initialize function is available, call it
      ;(iframeRef.contentWindow as any).initialize({ ...initData, embed_location: embedLocation }, callback)
    } else if (attempts < maxAttempts) {
      // Initialize function not ready yet, retry after a short delay
      setTimeout(() => tryInitialize(attempts + 1), retryInterval)
    } else {
      console.warn(
        `[${componentName}]: Initialize function not found in iframe after ${(maxAttempts * retryInterval) / 1000}s`,
        iframeRef.src,
      )
    }
  }

  // Check if iframe is already loaded
  if (iframeRef.contentWindow) {
    // Iframe is already loaded, start trying to initialize immediately
    tryInitialize()
  } else {
    // Iframe not loaded yet, wait for load event
    iframeRef.onload = () => {
      tryInitialize()
    }
  }
}
